<template>
  <div>
    <app-header title=""></app-header>

    <div class="container my-5">
      <div class="row mb-3">
        <div class="col d-flex align-items-center">
          <h2 class="mb-0">{{ cluster.name }}</h2>
        </div>
        <div class="col-auto d-flex align-items-center">
          <span class="status" :class="cluster.production ? 'status-primary' : 'status-warning'">
            {{ cluster.production ? 'Production' : 'Staging' }}
          </span>
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-body">
          <h3>Terraform State</h3>
          <div class="row">
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">State Bucket</label>
              <input type="text" class="form-control" readonly v-model="cluster.terraform_state_bucket" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">State Key</label>
              <input type="text" class="form-control" readonly v-model="cluster.terraform_state_key" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">State Region</label>
              <input type="text" class="form-control" readonly v-model="cluster.terraform_state_region" />
            </div>
          </div>

          <h3 class="mt-3">ECS Config</h3>
          <div class="row">
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">AWS Region</label>
              <input type="text" class="form-control" v-model="cluster.terraform_aws_region" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">AZ Count</label>
              <input type="text" class="form-control" v-model="cluster.terraform_az_count" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">Key Name</label>
              <input type="text" class="form-control" v-model="cluster.terraform_key_name" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">Instance Type</label>
              <input type="text" class="form-control" v-model="cluster.terraform_instance_type" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">ASG Min</label>
              <input type="text" class="form-control" v-model="cluster.terraform_asg_min" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">ASG Max</label>
              <input type="text" class="form-control" v-model="cluster.terraform_asg_max" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">ASG Desired</label>
              <input type="text" class="form-control" v-model="cluster.terraform_asg_desired" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">Admin CIDR</label>
              <input type="text" class="form-control" v-model="cluster.terraform_admin_cidr_ingress" />
            </div>
          </div>

          <h3 class="mt-3">RDS Master Credentials</h3>
          <div class="row">
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">Username</label>
              <input type="text" class="form-control" readonly v-model="cluster.terraform_rds_username" />
            </div>
            <div class="col-md-6 col-lg-4 mb-3">
              <label class="control-label">Password</label>
              <input type="password" class="form-control" v-model="cluster.terraform_rds_password" />
            </div>
          </div>

          <div class="text-end">
            <c-button @click="updateCluster" class="btn btn-rounded btn-primary">Save</c-button>
          </div>
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-body">
          <h3>Terraform CLI Commands</h3>

          <h4 class="mt-3">Dependencies</h4>
          <p class="mb-2">Download and follow readme</p>
          <pre class="cluster-command"><code>git clone git@code.codium.com.au:codium/stack.git</code></pre>

          <h4 class="mt-4">Workspaces <small class="text-muted">Each cluster is stored in a separate workspace</small></h4>
          <p class="mb-2">New cluster</p>
          <pre class="cluster-command"><code>terraform workspace new {{ cluster.name }}</code></pre>
          <p class="mt-3 mb-2">Update existing</p>
          <pre class="cluster-command"><code>terraform workspace select {{ cluster.name }}</code></pre>

          <h4 class="mt-4">Plan</h4>
          <p class="mb-2 text-muted">Checks the state stored in s3 against the actual resources and creates a plan of changes needed to make it match the config</p>
          <pre class="cluster-command"><code>terraform plan -out=plan.out -var rds_password={{ cluster.terraform_rds_password }} -var asg_desired={{ cluster.terraform_asg_desired }}</code></pre>

          <h4 class="mt-4">Apply</h4>
          <p class="mb-2 text-muted">The terraform apply command is used to apply the changes required to reach the desired state of the configuration, or the pre-determined set of actions generated by a terraform plan execution plan.</p>
          <div role="alert" class="alert alert-danger">
            <i class="fa fa-exclamation-triangle me-3 mt-1"></i>
            This will execute against the live resources, ensure output from plan command is correct.
            <a class="alert-link" href="https://www.terraform.io/docs/commands/apply.html" target="_blank" rel="noopener">Read more</a>.
          </div>
          <pre class="cluster-command"><code>terraform apply plan.out</code></pre>
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-body">
          <h3>Remove Cluster</h3>
          <p>
            Removing the cluster will delete all local information about the cluster. It will not delete the underlying infrastructure.<br>
            This action can only be performed on clusters with no applications.
          </p>
          <div class="text-end">
            <c-button @click="destroyCluster" class="btn btn-rounded btn-danger">Remove Cluster</c-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cluster from './../../models/cluster.js'
import { notify } from 'utils'

export default {
  data () {
    return {
      cluster: new Cluster()
    }
  },

  created () {
    this.fetchData()
  },

  methods: {
    fetchData () {
      return client({ path: '/clusters/' + this.$route.params.cluster_id }).then((response) => {
        this.cluster = response.entity
      })
    },

    updateCluster () {
      return client({ path: '/clusters/' + this.$route.params.cluster_id, entity: this.cluster, method: 'PUT' }).then(
        () => {
          notify.success('Cluster updated, run Terraform to apply changes')
        },
        (response) => {
          alert('Jenkins Response: ' + response.status.code + ' - ' + response.status.text)
        }
      )
    },

    destroyCluster () {
      return this.$swal({
        title: 'Confirm Delete',
        html: 'Are you sure you want to delete cluster ' + this.cluster.name + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Delete'
      }).then(() => {
        return client({ path: '/clusters/' + this.$route.params.cluster_id, entity: this.cluster, method: 'DELETE' }).then(
          () => {
            this.$router.push({ name: 'clusters.index' })
          },
          (response) => {
            alert('Jenkins Response: ' + response.status.code + ' - ' + response.status.text)
          }
        )
      }, () => {})
    }
  }
}
</script>

<style scoped>
.cluster-command {
  margin-bottom: 0;
  padding: 0.75rem 1rem;
  white-space: pre-wrap;
  word-break: break-all;
  background: var(--bs-light, #f8f9fa);
  border-radius: 0.375rem;
}
</style>
